import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileText, Download, RefreshCw, Send, Mail } from "lucide-react";

const prevMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setDate(0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const AdminAgencyReports = () => {
    const [data, setData] = useState(null);
    const [agencyId, setAgencyId] = useState("");
    const [period, setPeriod] = useState(prevMonth());
    const [sendEmail, setSendEmail] = useState(true);
    const [busy, setBusy] = useState(false);
    const [busyRow, setBusyRow] = useState(null);
    const [msg, setMsg] = useState(null);

    const load = () => api.get("/admin/agency-reports").then((r) => setData(r.data)).catch((e) => console.error(e));
    useEffect(() => { load(); }, []);

    const generate = async (aid, per, mail) => {
        setMsg(null);
        try {
            const r = await api.post("/admin/agency-reports/generate", { agency_id: aid, period: per, send_email: mail });
            setMsg({ ok: true, text: `Report ${r.data.report.period_label} generato per ${r.data.report.agency_name || r.data.report.agency_email}${mail ? ` · email: ${r.data.report.email_status}` : ""}` });
            await load();
        } catch (e) {
            setMsg({ ok: false, text: e?.response?.data?.detail || "Errore generazione report" });
        }
    };

    const generateManual = async () => {
        if (!agencyId) { setMsg({ ok: false, text: "Seleziona un'agenzia" }); return; }
        setBusy(true);
        await generate(agencyId, period, sendEmail);
        setBusy(false);
    };

    const regenerate = async (r) => {
        setBusyRow(r.id);
        await generate(r.agency_id, r.period, true);
        setBusyRow(null);
    };

    if (!data) return <div className="text-ink-400">Caricamento...</div>;

    return (
        <div data-testid="admin-reports-tab" className="space-y-8">
            <div className="border border-white/10 p-6 bg-ink-900/40">
                <div className="flex items-center gap-3 mb-2">
                    <FileText size={20} className="text-gold" />
                    <h2 className="font-serif text-2xl text-ink-50">Report agenzie</h2>
                </div>
                <p className="text-xs text-ink-400 mb-6">Il report mensile viene generato e inviato automaticamente il 1° di ogni mese a tutte le agenzie con piano attivo. Da qui puoi rigenerarlo e reinviarlo manualmente in qualsiasi momento.</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Agenzia</label>
                        <select data-testid="report-agency-select" value={agencyId} onChange={(e) => setAgencyId(e.target.value)} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm">
                            <option value="" className="bg-ink-950">— seleziona</option>
                            {(data.agencies || []).map((a) => (
                                <option key={a.user_id} value={a.user_id} className="bg-ink-950">{a.name} ({a.email})</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Mese</label>
                        <input data-testid="report-period-input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" />
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer pb-2.5">
                        <input data-testid="report-send-email" type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4 accent-gold" />
                        <span className="text-sm text-ink-100">Invia via email</span>
                    </label>
                    <button data-testid="report-generate-btn" onClick={generateManual} disabled={busy} className="cta-gold px-5 py-2.5 text-sm flex items-center justify-center gap-2">
                        <Send size={14} /> {busy ? "Generazione..." : "Genera report"}
                    </button>
                </div>
                {msg && (
                    <div data-testid="report-generate-msg" className={`mt-4 p-3 text-sm border ${msg.ok ? "border-green-700/40 bg-green-900/20 text-green-300" : "border-red-700/40 bg-red-900/20 text-red-300"}`}>
                        {msg.text}
                    </div>
                )}
            </div>

            <div className="border border-white/10">
                {(data.items || []).length === 0 ? (
                    <p data-testid="admin-reports-empty" className="p-6 text-sm text-ink-400">Nessun report ancora generato.</p>
                ) : (
                    <div className="divide-y divide-white/5">
                        {(data.items || []).map((r) => (
                            <div key={r.id} data-testid={`admin-report-row-${r.id}`} className="p-4 grid grid-cols-12 gap-3 items-center text-sm">
                                <div className="col-span-3">
                                    <div className="text-ink-50">{r.agency_name || "—"}</div>
                                    <div className="text-xs text-ink-400">{r.agency_email}</div>
                                </div>
                                <div className="col-span-2 font-serif text-gold">{r.period_label}</div>
                                <div className="col-span-2 text-xs text-ink-400">{new Date(r.generated_at).toLocaleString("it-IT")}</div>
                                <div className="col-span-3 text-xs">
                                    {r.email_status === "delivered" ? (
                                        <span className="px-2 py-1 bg-green-900/30 text-green-400 inline-flex items-center gap-1.5"><Mail size={11} /> Inviato a {r.sent_to}</span>
                                    ) : r.email_status === "failed" ? (
                                        <span className="px-2 py-1 bg-red-900/30 text-red-400">Invio fallito ({r.sent_to})</span>
                                    ) : (
                                        <span className="px-2 py-1 bg-ink-900 text-ink-400">Non inviato</span>
                                    )}
                                </div>
                                <div className="col-span-2 flex justify-end gap-2">
                                    <button
                                        data-testid={`admin-report-download-${r.id}`}
                                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/agency-reports/${r.id}/pdf`, "_blank")}
                                        className="p-2 text-ink-100 hover:text-gold" title="Scarica PDF"
                                    >
                                        <Download size={14} />
                                    </button>
                                    <button
                                        data-testid={`admin-report-regen-${r.id}`}
                                        onClick={() => regenerate(r)}
                                        disabled={busyRow === r.id}
                                        className="p-2 text-ink-100 hover:text-gold disabled:opacity-50" title="Rigenera e reinvia"
                                    >
                                        <RefreshCw size={14} className={busyRow === r.id ? "animate-spin" : ""} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
